import { useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Compass, Route, Clock, Map as MapIcon } from 'lucide-react';
import { useCommandStore, type TimelineMode } from '../../store/commandStore.js';
import { useTimelinePlayback } from '../../hooks/useTimelinePlayback.js';
import type { Place } from '@wingman/shared';

interface TimelineScrubberProps {
  places: Place[];
  routeGeometry: GeoJSON.LineString | null;
  onPlaceHighlight: (place: Place | null) => void;
  onVehicleProgress: (progress: number) => void;
  onHourChange: (hour: number) => void;
}

const MODE_TABS: { mode: TimelineMode; label: string; icon: typeof Compass }[] = [
  { mode: 'discovery', label: 'DISCOVERY', icon: Compass },
  { mode: 'route', label: 'ROUTE', icon: Route },
  { mode: 'timeofday', label: 'TIME', icon: Clock },
];

const SPEEDS = [1, 2, 3, 4];

export default function TimelineScrubber({
  places,
  routeGeometry,
  onPlaceHighlight,
  onVehicleProgress,
  onHourChange,
}: TimelineScrubberProps) {
  const { timelineMode, timelineCursor, timelinePlaying, timelineSpeed, setTimelineMode, setTimelineSpeed } = useCommandStore();
  const progressRef = useRef<HTMLDivElement>(null);

  const handleTick = useCallback((cursor: number, mode: TimelineMode) => {
    if (mode === 'discovery' && places.length > 0) {
      const idx = Math.floor(cursor * places.length);
      const place = places[Math.min(idx, places.length - 1)];
      onPlaceHighlight(place);
    } else if (mode === 'route') {
      onVehicleProgress(cursor);
    } else if (mode === 'timeofday') {
      onHourChange(Math.floor(cursor * 23));
    }
  }, [places, onPlaceHighlight, onVehicleProgress, onHourChange]);

  const { seek, togglePlay } = useTimelinePlayback({ onTick: handleTick });

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = progressRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width;
    seek(Math.max(0, Math.min(1, x)));
  }, [seek]);

  const handleModeChange = useCallback((mode: TimelineMode) => {
    setTimelineMode(mode);
    onPlaceHighlight(null);
    onVehicleProgress(0);
    onHourChange(0);
  }, [setTimelineMode, onPlaceHighlight, onVehicleProgress, onHourChange]);

  const label = (() => {
    if (timelineMode === 'discovery' && places.length > 0) {
      const idx = Math.floor(timelineCursor * places.length);
      const place = places[Math.min(idx, places.length - 1)];
      return place?.name || '';
    }
    if (timelineMode === 'route') {
      return `${Math.round(timelineCursor * 100)}% complete`;
    }
    if (timelineMode === 'timeofday') {
      const hour = Math.floor(timelineCursor * 23);
      return `${String(hour).padStart(2, '0')}:00`;
    }
    return '';
  })();

  const routeAvailable = routeGeometry !== null;
  const availableModes = MODE_TABS.filter((tab) => tab.mode !== 'route' || routeAvailable);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.28 }}
        className="timeline-scrubber"
      >
        <div className="timeline-mode-tabs">
          {availableModes.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.mode}
                type="button"
                className={`timeline-mode-tab ${timelineMode === tab.mode ? 'is-active' : ''}`}
                onClick={() => handleModeChange(tab.mode)}
              >
                <Icon size={11} />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="timeline-controls">
          <button type="button" className="timeline-play-btn" onClick={togglePlay}>
            {timelinePlaying ? <Pause size={16} /> : <Play size={16} />}
          </button>

          <div className="timeline-speed-selector">
            {SPEEDS.map((speed) => (
              <button
                key={speed}
                type="button"
                className={`timeline-speed-btn ${timelineSpeed === speed ? 'is-active' : ''}`}
                onClick={() => setTimelineSpeed(speed)}
              >
                {speed}×
              </button>
            ))}
          </div>

          <div className="timeline-progress-container" onClick={handleProgressClick} ref={progressRef}>
            <div className="timeline-progress-track">
              <div className="timeline-progress-fill" style={{ width: `${timelineCursor * 100}%` }} />
            </div>
            <div className="timeline-progress-thumb" style={{ left: `${timelineCursor * 100}%` }} />
          </div>

          <div className="timeline-label">{label}</div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
